import React, { useRef, useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  customStyle,
  fixedWidthCustomStyle,
  statusOptions,
  ratingOptions,
  DEFAULT_RECORDS_PER_PAGE,
  REGEX,
} from "components/Common/constants";
import ResetPassword from "components/Common/ResetPassword";
import CommonTable from "components/ReactTable/Table";
import RightPanelCard from "components/Common/RightPanelCard";
import { useNavigate } from "react-router-dom";
import { renderStars } from "components/Common/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import {
  getTechniciansList,
  getRegionList,
  getSpecializationList,
  addTechnician,
} from "actions/technicians";
import NotificationAlert from "react-notification-alert";
import { techniciansList } from "constants/technicians";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { RATING_RANGE } from "constants/Enum";

const initialFields = {
  firstName: {
    value: "",
    error: "",
    isChanged: false,
  },
  lastName: {
    value: "",
    error: "",
    isChanged: false,
  },
  email: {
    value: "",
    error: "",
    isChanged: false,
  },
  password: {
    value: "",
    error: "",
    isChanged: false,
  },
  phoneNo: {
    value: "",
    error: "",
    isChanged: false,
  },
  region: {
    value: null,
    error: "",
    isChanged: false,
  },
  specialization: {
    value: [],
    error: "",
    isChanged: false,
  },
  isOnlineJob: {
    value: "NO",
    error: "",
    isChanged: false,
  },
};

function TechnicianList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const regionList = useSelector((state) => state.technicians.regionList);
  const specializationList = useSelector(
    (state) => state.technicians.specializationList,
  );

  const notificationAlertRef = useRef(null);
  const technicianListData = useSelector(
    (state) => state.technicians.techniciansList,
  );

  const [technicianListState, setTechnicianListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [technicianFormData, setTechnicianFormData] = useState(initialFields);
  const [isModal, setIsModal] = useState({
    isOpen: false,
    mode: "",
  });
  const [passwordModal, setPasswordModal] = useState({ isOpen: false });
  const [showPassword, setShowPassword] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  const [searchState, setSearchState] = useState({
    searchStrings: "",
    status: null,
    region: null,
    specializations: null,
    rating: null,
    filterParams: [],
    isSearched: false,
    isLoading: false,
  });

  const [lastSearchedState, setLastSearchedState] = useState({
    searchStrings: "",
    status: null,
    region: null,
    specializations: null,
    rating: null,
  });

  const formattedTechnicianList = technicianListData?.data?.technician?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (technicianListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  const columns = [
  { 
    Header: "Sr. no.", 
    accessor: "srNo", 
    className: "col-srNo",
    width: 60,
    minWidth: 60,
    maxWidth: 80,
    centerAlign:true,
  },
  {
    Header: "Name",
    accessor: "fullName",
    className: "col-name",
    width: 180,
    minWidth: 140,
    maxWidth: 220
  },
  {
    Header: () => "Email",
    accessor: "email",
    className: "col-tech-email",
    width: 200,
    minWidth: 160,
    maxWidth: 260,
    Cell: ({ row }) => {
      const { email, phoneNo } = row?.original;
      return (
        <div className="">
          <div className="d-flex align-items-center mb-1">
            <i className="fa fa-envelope mr-2 text-light" /> {email}
          </div>
          <div className="d-flex align-items-center">
            <i className="fa fa-phone mr-2 text-light" />
            +966 {phoneNo}
          </div>
        </div>
      );
    },
  },
  {
    Header: "Specialization",
    accessor: "specializations",
    className: "col-specialization",
    width: 200,
    minWidth: 140,
    maxWidth: 280,
    Cell: ({ value }) => {
      const list = Array.isArray(value) ? value : value ? [value] : [];
      const visibleItems = list.slice(0, 3);
      const remainingCount = list.length - 3;

      return (
        <ul className="specialization-list mb-0">
          {visibleItems.map((item, index) => (
            <li key={index} className="li-color">
              <div className="specialization-item">
                <span className="spec-name">{item.name}</span>
                {index === visibleItems.length - 1 && remainingCount > 0 && (
                  <span className="remaining-count-badge">
                    +{remainingCount}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      );
    },
  },
  { 
    Header: "Online ?", 
    accessor: "isOnlineJob", 
    className: "col-online-job",
    width: 80,
    minWidth: 70,
    maxWidth: 100
  },
  {
    Header: () => "Region",
    accessor: "region",
    className: "col-region",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    Cell: ({ value }) => <div className="text-muted">{value?.name}</div>,
  },
  {
    Header: "Rating",
    accessor: "rating",
    className: "col-rating",
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    Cell: ({ value }) => renderStars(value),
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-status",
    width: 100,
    minWidth: 80,
    maxWidth: 120,
    Cell: ({ value }) => (
      <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
        {value === "DEACTIVATE" ? "suspended" : value}
      </Badge>
    ),
  },
  {
    Header: "Actions",
    accessor: "ID",
    className: "col-actions text-center",
    width: 80,
    minWidth: 60,
    maxWidth: 100,
    disableSortBy: true,
    Cell: ({ value }) => {
      const encodedId = urlEncodedId(value);
      return (
        <div className="actions-right d-flex justify-content-center">
          <Button
            className="btn-link btn-icon icon-info"
            id={`edit${value}`}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();

              window.open(
                `/admin/technicians/${encodedId}/details?edit=true`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <i className="tim-icons icon-pencil tim-md" />
          </Button>
          <UncontrolledTooltip delay={0} target={`edit${value}`}>
            Edit
          </UncontrolledTooltip>
        </div>
      );
    },
  },
];

  //api call for get technicians list
  useEffect(() => {
    getTechniciansList(dispatch, {
      pageNum: pageNum,
      filters: searchState.filterParams,
      searchString: searchState.searchStrings,
    });
    return () => {
      dispatch({ type: techniciansList, payload: {} }); //to clear the technician redux state on unmount
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (technicianListData?.code === 200) {
      setTechnicianListState({
        isLoading: false,
        error: "",
        noRecords: technicianListData?.data?.technician?.length === 0,
      });
      setSearchState((prev) => ({ ...prev, isLoading: false }));
    } else if (technicianListData?.code || technicianListData?.message) {
      setTechnicianListState({
        isLoading: false,
        error:
          technicianListData?.message ||
          "Something went Wrong! Please try again.",
        noRecords: false,
      });
    }
  }, [technicianListData]);

  //api to call region and specialization list
  useEffect(() => {
    getSpecializationList(dispatch);
    getRegionList(dispatch);
  }, []);

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState?.region?.ID)
      filterParams.push({
        field: "RegionID",
        condition: "eq",
        filterValues: [String(searchState.region.ID)],
      });

    if (searchState?.specializations?.length) {
      filterParams.push({
        field: "SpecializationID",
        condition: "eq",
        filterValues: searchState.specializations.map((s) => String(s.ID)),
      });
    }
    if (searchState?.rating?.label) {
      switch (searchState?.rating?.label) {
        case "0":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.ZERO_RANGE,
          });
          break;
        case "1":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.ONE_RANGE,
          });
          break;
        case "1.5":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.ONE_POINT_FIVE_RANGE,
          });
          break;
        case "2":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.TWO_RANGE,
          });
          break;
        case "2.5":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.TWO_POINT_FIVE_RANGE,
          });

          break;
        case "3":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.THREE_RANGE,
          });

          break;
        case "3.5":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.THREE_POINT_FIVE_RANGE,
          });

          break;
        case "4":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.FOUR_RANGE,
          });

          break;
        case "4.5":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.FOUR_POINT_FIVE_RANGE,
          });

          break;
        case "5":
          filterParams.push({
            field: "Rating",
            condition: "btw",
            filterValues: RATING_RANGE.FIVE_RANGE,
          });
          break;
        default:
          break;
      }
    }

    if (searchState?.status?.value)
      filterParams.push({
        field: "UserStatus",
        condition: "eq",
        filterValues: [searchState.status.value],
      });

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    // Check if current search state differs from last searched state
    const hasSearchChanged =
      searchState.searchStrings !== lastSearchedState.searchStrings ||
      searchState.status !== lastSearchedState.status ||
      searchState.region !== lastSearchedState.region ||
      JSON.stringify(searchState.specializations) !==
      JSON.stringify(lastSearchedState.specializations) ||
      searchState.rating !== lastSearchedState.rating;

    if (!hasSearchChanged) {
      // Nothing changed, don't trigger search
      return;
    }

    setSearchState((prev) => ({
      ...prev,
      filterParams: newfilterParams,
      isSearched: true,
      isLoading: true,
    }));

    setTechnicianListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    // Update last searched state
    setLastSearchedState({
      searchStrings: searchState.searchStrings,
      status: searchState.status,
      region: searchState.region,
      specializations: searchState.specializations,
      rating: searchState.rating,
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const clearSearch = () => {
    //set the page loader
    setTechnicianListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      searchStrings: "",
      status: null,
      region: null,
      specializations: null,
      rating: null,
      filterParams: [],
      isSearched: false,
      isLoading: false,
    });

    setLastSearchedState({
      searchStrings: "",
      status: null,
      region: null,
      specializations: null,
      rating: null,
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleRowOnClick = (rowData) => {
  const encodedId = urlEncodedId(rowData.ID);

  window.open(
    `/admin/technicians/${encodedId}/details`,
    "_blank",
    "noopener,noreferrer",
  );
};

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const SpecializationOption = (props) => {
    const { isSelected, label } = props;

    return (
      <components.Option {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => null}
            style={{ marginRight: 8 }}
          />
          <span>{label}</span>
        </div>
      </components.Option>
    );
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setTechnicianFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleChange = (fieldName, newValue) => {
    setTechnicianFormData((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: newValue,
        isChanged: false,
        error: "",
      },
    }));
  };
  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter a first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter a last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter an email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";
      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
            ? "Password must contain 8 characters"
            : "";
      case "phoneNo":
        return !value
          ? "Please enter your contact no"
          : value.length < 9
            ? "Contact number must be 9 digits"
            : !REGEX.CONTACT_REGEX.test(value)
              ? "Contact number must contain only valid digits"
              : "";
      case "region":
        return !value ? "Please select a region" : "";
      case "specialization":
        return !value || value.length === 0
          ? "Please select a specialization"
          : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newTechnician = { ...technicianFormData };

    //get all the keys in array
    Object.keys(newTechnician).forEach((key) => {
      newTechnician[key] = {
        ...newTechnician[key],
        error: getValidationError(key, newTechnician[key].value),
      };
    });
    setTechnicianFormData(newTechnician); //if error set error state
    return Object.values(newTechnician).every((item) => item.error === "");
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);

        // Convert specialization to array of arrays -> specialization (IDs)
        const specializationIDs = Array.isArray(
          technicianFormData.specialization.value,
        )
          ? technicianFormData.specialization.value.map((s) => s.ID)
          : technicianFormData.specialization.value?.ID
            ? [technicianFormData.specialization.value.ID]
            : [];
        const bodyData = {
          firstName: technicianFormData.firstName.value,
          lastName: technicianFormData.lastName.value,
          email: technicianFormData.email.value,
          password: technicianFormData.password.value,
          phoneNo: technicianFormData.phoneNo.value,
          isOnlineJob: technicianFormData.isOnlineJob.value,
          regionID: technicianFormData.region.value.ID,
          specialization: specializationIDs, // array format as accepted by backend
          technicianCategoryID: 1,
          countryID: 1,
          cityID: 1,
          roleID: 3,
        };

        const res = await addTechnician(bodyData);

        if (res.code === 200) {
          notify("success", res.message || "Technician added successfully");
          handleCloseModal();

          if (pageNum === 1) {
            getTechniciansList(
              dispatch,
              pageNum,
              searchState.filterParams,
              searchState.searchStrings,
            );
          } else {
            setPageNum(1);
          }
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
            : res?.message || "Failed to add technician";
          notify("danger", message);
        }
      } catch (error) {
        notify("danger", "Failed to add technician");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  const handleAddTechnician = () => {
    setIsModal({ isOpen: true, mode: "Add" });
  };
  const handleCloseModal = () => {
    setIsModal({ isOpen: false, mode: "" });
    setTechnicianFormData(initialFields);
    setShowPassword(false);
  };
  const handleCloseResetModal = () => {
    setPasswordModal({ isOpen: false });
  };

  // Custom RatingOption Renderer
  const RatingOption = (props) => {
    return (
      <components.Option {...props}>
        {renderStars(props.data.value)}
      </components.Option>
    );
  };

  // Custom SingleValue Rating Renderer
  const SingleValueRating = (props) => {
    return (
      <components.SingleValue {...props}>
        {renderStars(props.data.value)}
      </components.SingleValue>
    );
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Technicians
                </CardTitle>
                <Button
                  className="btn-round btn-style"
                  color="default"
                  size="md"
                  onClick={handleAddTechnician}
                >
                  <i className="fa fa-plus mr-1" />
                  Add Technician
                </Button>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-start filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name or email"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchStrings}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchStrings: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      isMulti
                      className="mr-2 mb-2 filter-select "
                      options={specializationList?.data?.specialization}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      placeholder="Specialization"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      value={searchState.specializations}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          specializations: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      components={{ Option: SpecializationOption }}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={ratingOptions}
                      placeholder="Rating"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{
                        Option: RatingOption,
                        SingleValue: SingleValueRating,
                      }}
                      onKeyDown={handleKeyDown}
                      value={searchState.rating}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          rating: selected,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={regionList?.data?.region}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      placeholder="Region"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={searchState.region}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          region: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={statusOptions}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={searchState.status}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          status: opt,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-3 mt-0 filter-btn btn-simple"
                      onClick={handleSearch}
                      disabled={searchState.isLoading} //diabled the btn when loading
                    >
                      {searchState.isLoading ? (
                        <>
                          <i className="fa fa-spinner fa-spin search-loader" />{" "}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {(searchState.filterParams.length > 0 ||
                      searchState.isSearched) && (
                        <Button
                          color="default"
                          className="d-flex align-items-center justify-content-center mt-0 mb-3 ml-2 filter-btn btn-simple"

                          onClick={clearSearch}
                        >
                          <i className="fa fa-times mr-1" /> Clear
                        </Button>
                      )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {technicianListData?.data?.totalRecords ? (
                    <span className="font-weight-bold">
                      {searchState.isSearched ||
                        searchState.filterParams?.length > 0
                        ? `Showing ${technicianListData?.data?.totalFilteredRecords} of ${technicianListData?.data?.totalRecords} Technicians`
                        : `Total Technicians: ${technicianListData?.data?.totalRecords}`}
                    </span>
                  ) : null}
                </div>
                {technicianListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : technicianListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {technicianListState.error}
                    </div>
                  </>
                ) : technicianListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No technicians added yet.
                  </div>
                ) : (
                  <div className="table-responsive technician-table">
                    <CommonTable
                      data={formattedTechnicianList || []}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 25}
                      rowClick={handleRowOnClick}
                      showPaginationBottom={true}
                      className="-striped -highlight table-hover"
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        technicianListData?.data?.totalFilteredRecords /
                        (technicianListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                      )}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <RightPanelCard isOpen={isModal.isOpen} onClose={handleCloseModal}>
        <Card className="mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2"
            tag="h4"
          >
            Add Technician
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </CardHeader>

          <CardBody>
            <Row>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${technicianFormData.firstName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    First Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    autoComplete="off"
                    value={technicianFormData.firstName.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={50}
                  />
                  {technicianFormData.firstName.error && (
                    <small className="validation-error">
                      {technicianFormData.firstName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${technicianFormData.lastName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Last Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    autoComplete="off"
                    value={technicianFormData.lastName.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={50}
                  />
                  {technicianFormData.lastName.error && (
                    <small className="validation-error">
                      {technicianFormData.lastName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${technicianFormData.email.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label for="email">
                    Email Address<span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter email address"
                    autoComplete="off"
                    value={technicianFormData.email.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={100}
                  />
                  {technicianFormData.email.error && (
                    <small className="validation-error">
                      {technicianFormData.email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              {isModal.mode === "Add" && (
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${technicianFormData.password.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label for="password">
                      Password <span className="validation-error">*</span>
                    </Label>
                    <InputGroup className="input-group">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        autoComplete="off"
                        value={technicianFormData.password.value}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        required
                        maxLength={16}
                      />

                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          className="d-flex align-items-center pointer"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <i className="fa fa-eye-slash" />
                          ) : (
                            <i className="fa fa-eye" />
                          )}
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                    {technicianFormData.password.error && (
                      <small className="validation-error">
                        {technicianFormData.password.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              )}

              <Col md="6" className="mb-2">
                <FormGroup
                  className={`has-label ${technicianFormData.phoneNo.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Contact Number <span className="validation-error">*</span>
                  </Label>

                  <InputGroup className="input-group">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText className="custom-border d-flex align-items-center">
                        <span className="mr-1">🇸🇦</span> +966
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      name="phoneNo"
                      type="tel"
                      placeholder="Enter contact number"
                      autoComplete="off"
                      value={technicianFormData.phoneNo.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      required
                      maxLength={9}
                    />
                  </InputGroup>
                  {technicianFormData.phoneNo.error && (
                    <small className="validation-error">
                      {technicianFormData.phoneNo.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>
                    Region <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${technicianFormData.region.error ? "is-invalid" : ""
                      }`}
                    options={regionList?.data?.region}
                    placeholder="Select region"
                    classNamePrefix="react-select"
                    styles={customStyle}
                    value={technicianFormData.region.value}
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) => handleChange("region", selected)}
                    onBlur={(e) =>
                      handleOnBlur("region", technicianFormData.region.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {technicianFormData.region.error && (
                    <small className="validation-error">
                      {technicianFormData.region.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>
                    Specialization <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${technicianFormData.specialization.error
                        ? "is-invalid"
                        : ""
                      }`}
                    options={specializationList?.data?.specialization}
                    placeholder="Select specialization category"
                    classNamePrefix="react-select "
                    styles={customStyle}
                    value={technicianFormData.specialization.value}
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) =>
                      handleChange("specialization", selected)
                    }
                    onBlur={(e) =>
                      handleOnBlur(
                        "specialization",
                        technicianFormData.specialization.value,
                      )
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                    isMulti
                    components={{ Option: SpecializationOption }}
                  />
                  {technicianFormData.specialization.error && (
                    <small className="validation-error">
                      {technicianFormData.specialization.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3 d-flex flex-column">
                <Label className="mb-1">
                  Can perform online jobs?{" "}
                  <span className="validation-error">*</span>
                </Label>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "38px" }}
                >
                  <Label className="custom-toggle custom-toggle-info mb-0">
                    <Input
                      type="checkbox"
                      checked={technicianFormData.isOnlineJob.value === "YES"}
                      name="isOnlineJob"
                      onChange={(e) =>
                        handleChange(
                          e.target.name,
                          e.target.checked === true ? "YES" : "NO",
                        )
                      }
                    />
                    <span
                      className="custom-toggle-slider rounded-circle"
                      data-label-off="NO"
                      data-label-on="YES"
                    ></span>
                  </Label>
                </div>
              </Col>
            </Row>
          </CardBody>
          <CardFooter>
            <div className="d-flex justify-content-end mb-0">
              <Button
                color="secondary"
                onClick={handleCloseModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="default"
                type="submit"
                onClick={() => {
                  handleSave();
                }}
                disabled={Object.values(technicianFormData).some(
                  (item) => item.error !== "",
                )}
              >
                {showLoaderOnClick ? (
                  <i className="fa fa-spinner fa-spin fa-lg text-white" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </RightPanelCard>
      <RightPanelCard
        isOpen={passwordModal.isOpen}
        onClose={handleCloseResetModal}
      >
        <ResetPassword
          isOpen={passwordModal.isOpen}
          handleCloseModal={handleCloseResetModal}
        />
      </RightPanelCard>
    </>
  );
}

export default TechnicianList;
